import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Sparkles, FileText } from "lucide-react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { errMsg } from "../lib/errors";

const iCl = "w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] focus:border-[var(--pa-gold)] focus:outline-none rounded text-sm text-[var(--pa-text)] p-2.5";

export default function TreatmentPlanNew() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [patients, setPatients] = useState([]);
  const [patientId, setPatientId] = useState(sp.get("patient_id") || "");
  const [feeCsv, setFeeCsv] = useState("");

  useEffect(() => {
    api.get("/patients").then((r) => setPatients(r.data || [])).catch(() => {});
  }, []);

  const parseFees = () => {
    if (!feeCsv.trim()) return null;
    const out = {};
    feeCsv.split(/\r?\n/).forEach((line) => {
      const [code, amt] = line.split(/[,\t]/).map((s) => s?.trim());
      if (code && amt && !isNaN(parseFloat(amt))) out[code.toUpperCase()] = parseFloat(amt);
    });
    return Object.keys(out).length ? out : null;
  };

  const submit = async () => {
    if (!patientId) { toast.error("Select a patient"); return; }
    setBusy(true);
    try {
      const r = await api.post("/treatment-plan/synthesize", {
        patient_id: patientId,
        practice_fee_schedule: parseFees(),
      });
      toast.success("Treatment plan generated");
      nav(`/treatment-plan/${r.data.id}`);
    } catch (e) {
      toast.error(errMsg(e, "Treatment plan synthesis failed"));
    } finally { setBusy(false); }
  };

  return (
    <Layout>
      <div className="kicker">Phase 2 · Treatment Planning</div>
      <h1 data-testid="tx-plan-title" className="font-serif text-3xl text-[var(--pa-text)] mt-1 flex items-center gap-3">
        <FileText size={22} className="text-[var(--pa-gold)]" /> AI Treatment Planning Engine
      </h1>
      <p className="mt-2 text-sm text-[var(--pa-text-muted)] max-w-2xl">
        Synthesizes every recent ProsthoAI module output for this patient — CCA, TMD, Perio-Prostho,
        and any of the 11 clinical modules — into a 5-phase plan with three options (Ideal / Alternative / Minimal),
        SOAP note, and pre-auth letter.
      </p>

      <div className="mt-7 max-w-2xl space-y-5">
        <div>
          <label className="kicker block mb-1">Patient</label>
          <select value={patientId} onChange={(e) => setPatientId(e.target.value)} className={iCl} data-testid="tx-plan-patient">
            <option value="">— Select patient —</option>
            {patients.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="kicker block mb-1">Practice Fee Schedule (optional CSV — CDT,Fee per line)</label>
          <textarea value={feeCsv} onChange={(e) => setFeeCsv(e.target.value)} rows={4} data-testid="tx-plan-fees"
            placeholder="D6010,2200&#10;D7140,250&#10;D2740,1300"
            className={`${iCl} font-mono text-xs`} />
          <p className="text-[11px] text-[var(--pa-text-dim)] mt-1">If blank, the AI applies regional UCR estimates.</p>
        </div>

        <button onClick={submit} disabled={busy} data-testid="tx-plan-submit"
          className="flex items-center gap-2 bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold px-6 py-3 rounded-md hover:bg-[var(--pa-gold-hover)] transition disabled:opacity-60">
          {busy ? <><Loader2 size={14} className="animate-spin" /> Synthesizing across all modules…</> : <><Sparkles size={14} /> Generate Treatment Plan</>}
        </button>
      </div>
    </Layout>
  );
}
